import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, CircularProgress, Chip, Typography, Alert,
} from '@mui/material';
import { fetchCustomerProductPurchases } from '../api';
import { formatDate, headerCell, money, surface } from '../format';

const ProductPurchasesTab = ({ customerId }) => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetchCustomerProductPurchases(customerId, { page, limit: 10 });
                if (cancelled) return;
                setRows(res.data.data || []);
                setTotalPages(res.data.totalPages || 1);
                setTotal(res.data.total || 0);
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || 'Failed to load product purchases');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [customerId, page]);

    return (
        <Box>
            <Typography sx={{ color: '#6b7280', fontSize: '0.8125rem', mb: 1.5 }}>
                {total} product purchase{total === 1 ? '' : 's'}
            </Typography>
            <Paper elevation={0} sx={surface}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['Event', 'Order date', 'Amount', 'Exhibitors', 'Status', 'Order ID'].map((h) => (
                                        <TableCell key={h} sx={headerCell}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                                            No product purchases yet.
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell sx={{ color: '#111827', fontWeight: 500 }}>
                                            {row.event_name || '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: '#6b7280' }}>
                                            {formatDate(row.order_date || row.createdAt)}
                                        </TableCell>
                                        <TableCell>{money(row.amount_paid)}</TableCell>
                                        <TableCell>{row.exhibitors_count ?? '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.status || '—'}
                                                sx={{ textTransform: 'capitalize', bgcolor: '#f3f4f6' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: '#6b7280' }}>{row.order_id || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" />
                </Box>
            )}
        </Box>
    );
};

export default ProductPurchasesTab;
